import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import mime from 'mime';

import * as filetreeClient from 'client/app/api/filetree';
import { MUTATION_MARK_EXECUTION_STAGE_INSTRUCTIONS_DOWNLOADED } from 'client/app/api/gql/mutations';
import { QUERY_EXECUTION } from 'client/app/api/gql/queries';
import { SimulationTask } from 'client/app/apps/execution-details/types';
import { useSnackbarManager } from 'common/ui/components/SnackbarManager';

const INSTRUCTIONS_FILE_EXTENSIONS_REGEX = /^.*\.(txt|csv|tar.gz|sqlite)$/;

export default function useDownloadInstructions() {
  const listFiletreeFiles = filetreeClient.useList();
  const userDownloadFile = filetreeClient.useUserDownloadFile();
  const { showError } = useSnackbarManager();
  const [markExecutionStageInstructionsDownloaded] = useMutation(
    MUTATION_MARK_EXECUTION_STAGE_INSTRUCTIONS_DOWNLOADED,
  );

  return useCallback(
    async (
      executionId: ExecutionId,
      simulationId: SimulationId,
      simulationName: string,
      executionStageId: ExecutionStageId,
      stageNumber: number,
      task: SimulationTask,
      markAsInstructionsDownloaded: boolean,
    ): Promise<void> => {
      try {
        const { device, filetreeLink } = task;
        if (!filetreeLink) {
          showError(
            'Cannot find instructions file. Liquid handling task has no filetree link.',
          );
          return;
        }
        if (!device) {
          showError('Cannot find instructions file. Liquid handling task has no device.');
          return;
        }

        // Map additional content types to the extensions.
        // The file with instruction comes in different formats, depending on the device:
        // .sqlite for Gilson, .tar.gz for Tecan + Hamilton, .csv for Dragonfly, .txt for Mantis
        const mapContentTypeToExtension: Partial<{
          [contentType: string]: string;
        }> = {
          'application/gzip': 'tar.gz',
          'application/x-gzip': 'tar.gz',
          'application/x-sqlite3': 'sqlite', // legacy mime type
          'application/vnd.sqlite3': 'sqlite', // sqlite mime type as of 2018
        };

        const filesForTask = await listFiletreeFiles(filetreeLink);
        let instructionsFile = filesForTask.items.find(fileInfo =>
          // The file with device instructions is stored in filetree under an auto-generated
          // name, for example:
          // "b6b66249-97b3-4cec-9408-316b448d5695-bb867947-5f0e-4664-81c5-c8275a49b7f6-dQgEQHdJUcs.sqlite"
          // The name starts with the UUID of the device.
          fileInfo.name.startsWith(device.id),
        );
        if (!instructionsFile) {
          // This is an older simulation migrated from the legacy datastore. The legacy
          // filetree structure for these old simulations looks "special".
          // There is a file in filetree with a name like:
          // "6C12QC7J4APBVK7YHEQA6AMMC9-13Mc54D39iB-2ik9IjwWWG0.sqlite"
          // This is the file we want to find. We simply look for a filename that starts
          // with a legacy id. This is safe because old simulations always had at most one
          // file that matches this pattern:
          const startsWithLegacyIdRegex = /^[A-Z0-9]{26}-/;
          const legacyInstructionsFile = filesForTask.items.find(fileInfo =>
            startsWithLegacyIdRegex.test(fileInfo.name),
          );
          if (!legacyInstructionsFile) {
            showError(
              'File with liquid handling instructions is missing. Not found in filetree: ' +
                task.filetreeLink,
            );
            return;
          }
          instructionsFile = legacyInstructionsFile;
        }

        const filePath = instructionsFile.ftl;
        let fileExtension: string | null = null;

        const fileExtensionOutput = INSTRUCTIONS_FILE_EXTENSIONS_REGEX.exec(
          instructionsFile.name,
        );
        if (fileExtensionOutput) {
          fileExtension = fileExtensionOutput[1]; // if some extension matches it is a 2nd element
        } else {
          fileExtension =
            mapContentTypeToExtension[instructionsFile.contentType ?? ''] ||
            mime.getExtension(instructionsFile.contentType ?? '');
        }

        if (!fileExtension) {
          showError(
            `Instructions file: No known file type for content type ${instructionsFile.contentType} ` +
              `link: ${instructionsFile.ftl}`,
          );
          return;
        }

        // We include the simulation name/id and stage number on the raw file so that
        // users can distinguish files from each other on their local filesystem.
        const deviceInstructionsFileName = `device_instructions.${fileExtension}`;
        const deviceInstructionsRawFileName = `${simulationName}_${simulationId}_stage_${
          stageNumber + 1
        }_${deviceInstructionsFileName}`;
        await userDownloadFile(filePath, deviceInstructionsRawFileName);

        if (markAsInstructionsDownloaded) {
          await markExecutionStageInstructionsDownloaded({
            variables: {
              executionStageId: executionStageId,
            },
            // The overall execution status may change as a result of this mutation,
            // so we need to refetch it
            refetchQueries: [
              {
                query: QUERY_EXECUTION,
                variables: { id: executionId },
              },
            ],
            onError(error) {
              showError(error.message);
            },
          });
        }
      } catch (error) {
        showError(error.message);
      }
    },
    [
      showError,
      markExecutionStageInstructionsDownloaded,
      listFiletreeFiles,
      userDownloadFile,
    ],
  );
}
